import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import Ionicons from '@expo/vector-icons/Ionicons';
import {
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { SUPPORT_URL, verifyFailure } from '@/lib/auth-error';
import { otpRetrySeconds, otpSendLimitFrom } from '@/lib/otp-limit';
import { formatRetryCountdown } from '@/lib/retry-countdown';
import { createLatch } from '@/lib/single-tap';
import {
  MissingPendingEmailError,
  useAuthStore,
} from '@/state/auth-store';
import { authStrings as strings } from '@/i18n/features/auth.i18n';
import {
  type AppColors,
  useAppTheme,
} from '@/theme/app-theme';

const FONT_FAMILY = Platform.select({
  ios: 'Avenir Next',
  android: 'sans-serif',
  default: undefined,
});

const RESEND_COOLDOWN_SECONDS = 30;

/** What the server accepts, and therefore what the box must collect. */
const CODE_LENGTH = 6;

export default function Verify() {
  const { t } = useTranslation();
  const router = useRouter();

  const {
    colors,
    isDark,
    statusBarStyle,
  } = useAppTheme();

  const styles = useMemo(
    () => createStyles(colors, isDark),
    [colors, isDark],
  );

  const { height, width } =
    useWindowDimensions();

  const compactHeight = height < 900;
  const veryCompactHeight = height < 720;
  const wideScreen = width >= 620;

  const horizontalPadding =
    wideScreen ? 40 : 22;

  const email = useAuthStore(
    (state) => state.pendingEmail,
  );

  const verify = useAuthStore(
    (state) => state.verify,
  );

  const startEmail = useAuthStore(
    (state) => state.startEmail,
  );

  const restorePendingEmail = useAuthStore(
    (state) => state.restorePendingEmail,
  );

  /**
   * The route's own copy of the address, which survives what the store does
   * not: Expo Router restores params along with the route after the OS has
   * reclaimed the process — and leaving the app to read the code is the one
   * thing every user of this screen has to do.
   */
  const params = useLocalSearchParams<{ email?: string }>();

  /**
   * Whether we are still looking for the address. Distinct from "there isn't
   * one": the disk read is asynchronous, and treating an unfinished read as a
   * failure would flash the start-again panel at every user on every open.
   */
  const [recovering, setRecovering] = useState(
    () => !useAuthStore.getState().pendingEmail,
  );

  useEffect(() => {
    if (!recovering) return;
    let cancelled = false;
    void (async () => {
      await restorePendingEmail(params.email ?? null);
      if (!cancelled) setRecovering(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [recovering, params.email, restorePendingEmail]);

  /**
   * There is no address, and the search is over. The screen cannot verify
   * anything, so it must not offer a code box — one that can only ever refuse
   * is what turned this bug into "the right code does not work".
   */
  const lost = !recovering && !email;

  const [code, setCode] = useState('');
  const [error, setError] =
    useState<string>();
  /**
   * Whether the refusal on screen is a suspension.
   *
   * Tracked beside the message rather than inferred from it: the sentence is
   * translated into seven languages, so matching on its text would break in six
   * of them the day one is reworded. A suspension is the one refusal that
   * cannot be retried, and the only one that needs somewhere to go.
   */
  const [suspended, setSuspended] = useState(false);
  // Synchronous, unlike `loading` and `resendCooldown`, which are both state
  // and so only stop a second press one render too late. Verifying twice races
  // a code that the first request has already consumed; resending twice mints a
  // second code that kills the first one's email.
  const submitLatch = useRef(
    createLatch(),
  ).current;
  const resendLatch = useRef(
    createLatch(),
  ).current;

  const [loading, setLoading] =
    useState(false);
  const [
    resendCooldown,
    setResendCooldown,
  ] = useState(RESEND_COOLDOWN_SECONDS);
  const codeInputRef =
    useRef<TextInput>(null);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setResendCooldown((current) =>
        Math.max(0, current - 1),
      );
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  /**
   * The classification lives in `lib/auth-error.ts`, where it can be tested;
   * this is only the copy for each outcome.
   */
  /**
   * A 429 carrying the address's send budget knows exactly how long the wait
   * is, so it says so. Counted from NOW rather than from when the response was
   * built — the user may read this a minute later, and a stale figure invites a
   * retry that gets refused again.
   */
  const limitMessage = (err: unknown): string | null => {
    const refusal = otpSendLimitFrom(err);
    if (!refusal) return null;
    return t(strings.tooManyAttemptsError, {
      countdown: formatRetryCountdown(otpRetrySeconds(refusal)),
    });
  };

  const messageFor = (err: unknown): string => {
    const limited = limitMessage(err);
    if (limited) return limited;
    const failure = verifyFailure(err);
    // Before the code checks: this is not a verdict on the code, and every
    // sentence below it says or implies that it is.
    if (failure === "suspended") return t(strings.suspendedError);
    if (failure === "lost") return t(strings.lostBody);
    if (failure === "burned") return t(strings.burnedCodeError);
    if (failure === "offline") return t(strings.offlineError);
    if (failure === "expired") return t(strings.expiredCodeError);
    if (failure === "length") return t(strings.codeLengthError);
    if (failure === "rate_limited") return t(strings.tooManyError);
    if (failure === "service") return t(strings.serviceError);
    return t(strings.invalidCodeError);
  };

  async function onVerify() {
    if (!submitLatch.claim()) {
      return;
    }

    try {
      await submitCode();
    } finally {
      submitLatch.release();
    }
  }

  async function submitCode() {
    // Six, because that is what the server accepts. Five digits used to be sent
    // and come back a 400, which the screen then reported as a wrong code.
    if (code.trim().length < CODE_LENGTH) {
      setSuspended(false);
      setError(
        t(strings.codeLengthError),
      );

      return;
    }

    setError(undefined);
    setSuspended(false);
    setLoading(true);

    try {
      await verify(code);

      // Navigation guard takes over once
      // status becomes authenticated.
    } catch (err) {
      // The code is only cleared when the code is what failed. Wiping it after
      // a rate limit or a dropped connection makes the user retype something
      // that was never the problem.
      const outcome = verifyFailure(err);
      if (outcome === "invalid" || outcome === "burned") setCode("");
      setSuspended(outcome === "suspended");
      setError(messageFor(err));
      requestAnimationFrame(() => {
        codeInputRef.current?.focus();
      });
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    if (!resendLatch.claim()) {
      return;
    }

    try {
      await requestNewCode();
    } finally {
      resendLatch.release();
    }
  }

  async function requestNewCode() {
    if (resendCooldown > 0) {
      return;
    }
    // Never a silent return. With no address this button did nothing at all —
    // no request, no countdown, no error — which is exactly how it looked from
    // the outside: a dead button on a screen that also rejected a correct code.
    if (!email) {
      setError(t(strings.lostBody));
      return;
    }

    setError(undefined);
    setCode('');
    // Optimistic: the countdown starts on the tap, not when the network
    // answers. A button that waits a second and a half before acknowledging
    // reads as broken, and the user taps it again.
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    requestAnimationFrame(() => {
      codeInputRef.current?.focus();
    });

    try {
      await startEmail(email);
    } catch (err) {
      // Rolled back, because the cooldown was a promise that a code is coming.
      setResendCooldown(0);
      setSuspended(false);
      const limited = limitMessage(err);
      setError(
        limited ??
          (err instanceof ApiError && err.status === 429
            ? t(strings.tooManyError)
            : t(strings.resendError)),
      );
    }
  }

  function onChangeCode(value: string) {
    const nextCode = value
      .replace(/[^0-9]/g, '')
      .slice(0, 6);

    setCode(nextCode);

    if (error) {
      setError(undefined);
      setSuspended(false);
    }
  }

  function onChangeEmail() {
    router.replace('/(auth)/welcome');
  }

  return (
    <SafeAreaView
      style={styles.screen}
      edges={['top', 'bottom']}>
      <StatusBar
        style={statusBarStyle}
      />

      <KeyboardAvoidingView
        behavior="padding"
        style={styles.keyboardView}>
        <ScrollView
          bounces={false}
          keyboardDismissMode={
            Platform.OS === 'ios'
              ? 'interactive'
              : 'on-drag'
          }
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingHorizontal:
                horizontalPadding,
            },
            compactHeight &&
              styles.scrollContentCompact,
            veryCompactHeight &&
              styles.scrollContentVeryCompact,
          ]}>
          <View style={styles.pageContainer}>
            <View style={styles.header}>
              <Text
                style={[
                  styles.logo,
                  compactHeight &&
                    styles.logoCompact,
                  veryCompactHeight &&
                    styles.logoVeryCompact,
                ]}>
                <Text style={styles.logoDark}>
                  opn
                </Text>

                <Text style={styles.logoBlue}>
                  cue.
                </Text>
              </Text>
            </View>

            <View
              style={[
                styles.content,
                compactHeight &&
                  styles.contentCompact,
                veryCompactHeight &&
                  styles.contentVeryCompact,
              ]}>
              <View
                style={[
                  styles.headingGroup,
                  compactHeight &&
                    styles.headingGroupCompact,
                  veryCompactHeight &&
                    styles.headingGroupVeryCompact,
                ]}>
                <Text
                  style={[
                    styles.title,
                    compactHeight &&
                      styles.titleCompact,
                    veryCompactHeight &&
                      styles.titleVeryCompact,
                  ]}>
                  {lost
                    ? t(strings.lostTitle)
                    : t(strings.verifyTitle)}
                </Text>

                <Text
                  style={[
                    styles.subtitle,
                    compactHeight &&
                      styles.subtitleCompact,
                    veryCompactHeight &&
                      styles.subtitleVeryCompact,
                  ]}>
                  {lost
                    ? t(strings.lostBody)
                    : t(strings.codeSent)}
                </Text>

                {/* The address, and ONLY when we have it. This used to fall
                    back to "your inbox", which let a screen that could not
                    verify anything look exactly like one that could — the
                    detail that hid this bug from everyone who hit it. */}
                {email ? (
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.email,
                      compactHeight &&
                        styles.emailCompact,
                      veryCompactHeight &&
                        styles.emailVeryCompact,
                    ]}>
                    {email}
                  </Text>
                ) : null}
              </View>

              {recovering ? (
                <View style={styles.recovering}>
                  <ActivityIndicator color={colors.primary} />
                </View>
              ) : null}

              {lost ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.lostAction)}
                  onPress={onChangeEmail}
                  style={({ pressed }) => [
                    styles.verifyButton,
                    compactHeight &&
                      styles.verifyButtonCompact,
                    pressed && styles.buttonPressed,
                  ]}>
                  <Text
                    style={[
                      styles.verifyButtonText,
                      compactHeight &&
                        styles.verifyButtonTextCompact,
                    ]}>
                    {t(strings.lostAction)}
                  </Text>
                </Pressable>
              ) : null}

              <View
                style={[
                  styles.verifyCard,
                  recovering || lost
                    ? styles.hidden
                    : null,
                  compactHeight &&
                    styles.verifyCardCompact,
                  veryCompactHeight &&
                    styles.verifyCardVeryCompact,
                ]}>
                <View>
                  <Text
                    style={[
                      styles.inputLabel,
                      veryCompactHeight &&
                        styles.inputLabelVeryCompact,
                    ]}>
                    {t(strings.codeLabel)}
                  </Text>

                  <View
                    style={[
                      styles.codeInputContainer,
                      compactHeight &&
                        styles.codeInputContainerCompact,
                      veryCompactHeight &&
                        styles.codeInputContainerVeryCompact,
                      error
                        ? styles.codeInputContainerError
                        : null,
                    ]}>
                    <TextInput
                      ref={codeInputRef}
                      value={code}
                      onChangeText={onChangeCode}
                      placeholder="••••••"
                      placeholderTextColor={
                        isDark
                          ? colors.textMuted
                          : '#A5B0C1'
                      }
                      keyboardType="number-pad"
                      keyboardAppearance={
                        isDark
                          ? 'dark'
                          : 'light'
                      }
                      textContentType="oneTimeCode"
                      autoComplete="sms-otp"
                      autoFocus
                      maxLength={6}
                      returnKeyType="done"
                      onSubmitEditing={onVerify}
                      selectionColor={
                        isDark
                          ? colors.primary
                          : '#2468D8'
                      }
                      style={[
                        styles.codeInput,
                        compactHeight &&
                          styles.codeInputCompact,
                        veryCompactHeight &&
                          styles.codeInputVeryCompact,
                      ]}
                    />
                  </View>

                  {error ? (
                    <View style={styles.errorRow}>
                      <Ionicons
                        name="alert-circle-outline"
                        size={14}
                        color={
                          isDark
                            ? colors.dangerStrong
                            : '#D5424D'
                        }
                      />

                      <Text style={styles.errorText}>
                        {error}
                      </Text>
                    </View>
                  ) : null}

                  {/*
                    The whole redress route, and it has to be reachable from
                    THIS screen. A suspended account is signed out and refused
                    at sign-in, so there is no session left to open Help with —
                    "contact support" without an address would be a sentence
                    pointing at nothing.

                    The URL is its own line rather than a phrase inside the
                    sentence: the sentence is translated into seven languages,
                    and a link woven through one of them is six chances to
                    break the grammar or lose the tap target. A bare address
                    reads the same in all of them.
                  */}
                  {suspended ? (
                    <Pressable
                      accessibilityRole="link"
                      accessibilityLabel={SUPPORT_URL}
                      onPress={() => {
                        // Swallowed: a device with no browser to hand this to
                        // must not turn a refusal into a crash. The address is
                        // on screen either way, which is why it is shown as
                        // text and not hidden behind a label.
                        void Linking.openURL(SUPPORT_URL).catch(() => undefined);
                      }}
                      style={styles.supportLinkRow}
                    >
                      <Text style={styles.supportLinkText}>{SUPPORT_URL}</Text>
                    </Pressable>
                  ) : null}
                </View>

          

                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.verify)}
                  disabled={loading}
                  onPress={onVerify}
                  style={({ pressed }) => [
                    styles.verifyButton,
                    compactHeight &&
                      styles.verifyButtonCompact,
                    veryCompactHeight &&
                      styles.verifyButtonVeryCompact,
                    pressed &&
                      !loading &&
                      styles.buttonPressed,
                    loading &&
                      styles.buttonDisabled,
                  ]}>
                  {loading ? (
                    <ActivityIndicator
                      color={colors.white}
                    />
                  ) : (
                    <>
                      <Text
                        style={[
                          styles.verifyButtonText,
                          compactHeight &&
                            styles.verifyButtonTextCompact,
                        ]}>
                        {t(strings.verify)}
                      </Text>

                      <Ionicons
                        name="arrow-forward"
                        size={
                          veryCompactHeight
                            ? 17
                            : compactHeight
                              ? 18
                              : 20
                        }
                        color={colors.white}
                      />
                    </>
                  )}
                </Pressable>

                <View
                  style={[
                    styles.resendSection,
                    veryCompactHeight &&
                      styles.resendSectionVeryCompact,
                  ]}>
                  <Text
                    style={[
                      styles.resendPrompt,
                      veryCompactHeight &&
                        styles.resendPromptVeryCompact,
                    ]}>
                    {t(strings.didntReceive)}
                  </Text>

                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t(strings.resendCode)}
                    disabled={resendCooldown > 0}
                    onPress={onResend}
                    hitSlop={10}
                    style={({ pressed }) => [
                      styles.resendButton,
                      resendCooldown > 0 &&
                        styles.resendButtonDisabled,
                      pressed &&
                        resendCooldown <= 0 &&
                        styles.resendPressed,
                    ]}>
                    {resendCooldown > 0 ? (
                      <>
                        <Ionicons
                          name="time-outline"
                          size={17}
                          color={
                            isDark
                              ? colors.iconMuted
                              : '#8390A4'
                          }
                        />

                        <Text
                          style={styles.resentText}>
                          {t(strings.resendIn, { seconds: resendCooldown })}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Ionicons
                          name="refresh-outline"
                          size={17}
                          color={
                            isDark
                              ? colors.primaryStrong
                              : '#2463CB'
                          }
                        />

                        <Text
                          style={styles.resendText}>
                          {t(strings.resendCode)}
                        </Text>
                      </>
                    )}
                  </Pressable>
                </View>
              </View>
            </View>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(strings.changeEmail)}
              onPress={onChangeEmail}
              style={({ pressed }) => [
                styles.backButton,
                compactHeight &&
                  styles.backButtonCompact,
                pressed &&
                  styles.backButtonPressed,
              ]}>
              <Ionicons
                name="arrow-back"
                size={17}
                color={
                  isDark
                    ? colors.primaryStrong
                    : '#2463CB'
                }
              />

              <Text style={styles.backButtonText}>
                {t(strings.changeEmail)}
              </Text>
            </Pressable>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function createStyles(
  colors: AppColors,
  isDark: boolean,
) {
  return StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: isDark
        ? colors.background
        : '#FCFDFF',
    },

    keyboardView: {
      flex: 1,
    },

    scrollContent: {
      flexGrow: 1,
      justifyContent: 'center',
      paddingTop: 18,
      paddingBottom: 14,
    },

    scrollContentCompact: {
      paddingTop: 8,
      paddingBottom: 7,
    },

    scrollContentVeryCompact: {
      paddingTop: 3,
      paddingBottom: 3,
    },

    pageContainer: {
      width: '100%',
      maxWidth: 540,
      alignSelf: 'center',
    },

    header: {
      alignItems: 'center',
    },

    logo: {
      fontFamily: FONT_FAMILY,
      fontSize: 42,
      lineHeight: 49,
      fontWeight: '500',
      letterSpacing: -3,
    },

    logoCompact: {
      fontSize: 36,
      lineHeight: 42,
      letterSpacing: -2.6,
    },

    logoVeryCompact: {
      fontSize: 32,
      lineHeight: 37,
      letterSpacing: -2.3,
    },

    logoDark: {
      color: isDark
        ? colors.text
        : '#092D5B',
    },

    logoBlue: {
      color: isDark
        ? colors.primary
        : '#2468D4',
    },

    content: {
      alignItems: 'center',
      paddingTop: 18,
      paddingBottom: 18,
    },

    contentCompact: {
      paddingTop: 9,
      paddingBottom: 9,
    },

    contentVeryCompact: {
      paddingTop: 5,
      paddingBottom: 5,
    },

    headingGroup: {
      alignItems: 'center',
      marginBottom: 22,
    },

    headingGroupCompact: {
      marginBottom: 14,
    },

    headingGroupVeryCompact: {
      marginBottom: 10,
    },

    title: {
      color: isDark
        ? colors.text
        : '#102D51',
      fontFamily: FONT_FAMILY,
      fontSize: 28,
      lineHeight: 34,
      fontWeight: '700',
      letterSpacing: -0.6,
      textAlign: 'center',
    },

    titleCompact: {
      fontSize: 24,
      lineHeight: 29,
    },

    titleVeryCompact: {
      fontSize: 21,
      lineHeight: 25,
    },

    subtitle: {
      marginTop: 10,
      color: isDark
        ? colors.textSecondary
        : '#7B879A',
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 21,
      fontWeight: '500',
      textAlign: 'center',
    },

    subtitleCompact: {
      marginTop: 7,
      fontSize: 13.5,
      lineHeight: 18,
    },

    subtitleVeryCompact: {
      marginTop: 5,
      fontSize: 12.5,
      lineHeight: 16,
    },

    email: {
      maxWidth: 300,
      marginTop: 3,
      color: isDark
        ? colors.primaryStrong
        : '#235FC4',
      fontFamily: FONT_FAMILY,
      fontSize: 15,
      lineHeight: 21,
      fontWeight: '700',
      textAlign: 'center',
    },

    emailCompact: {
      fontSize: 13.5,
      lineHeight: 18,
    },

    emailVeryCompact: {
      maxWidth: 270,
      fontSize: 12.5,
      lineHeight: 16,
    },

    verifyCard: {
      // Fills the page container (like welcome's card) rather than capping
      // narrower — keeps the two screens visually consistent in width.
      width: '100%',
      alignSelf: 'center',
      padding: 21,
      gap: 18,
      borderRadius: 22,
      borderWidth: 1,
      borderColor: isDark
        ? colors.border
        : '#E1E8F1',
      backgroundColor: isDark
        ? colors.surface
        : '#FFFFFF',
      shadowColor: isDark
        ? colors.shadow
        : '#6E86A8',
      shadowOpacity: isDark
        ? 0.28
        : 0.13,
      shadowRadius: 20,
      shadowOffset: {
        width: 0,
        height: 10,
      },
      elevation: 4,
    },

    verifyCardCompact: {
      padding: 17,
      gap: 14,
      borderRadius: 19,
    },

    verifyCardVeryCompact: {
      padding: 13,
      gap: 9,
      borderRadius: 16,
    },

    inputLabel: {
      marginBottom: 8,
      color: isDark
        ? colors.textBody
        : '#253E61',
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: '600',
    },

    inputLabelVeryCompact: {
      marginBottom: 6,
      fontSize: 12,
    },

    codeInputContainer: {
      height: 60,
      paddingHorizontal: 12,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 13,
      borderWidth: 1,
      borderColor: isDark
        ? colors.borderSoft
        : '#D6DFEB',
      backgroundColor: isDark
        ? colors.surfaceMuted
        : '#FAFBFD',
    },

    codeInputContainerCompact: {
      height: 52,
      borderRadius: 12,
    },

    codeInputContainerVeryCompact: {
      height: 46,
      borderRadius: 10,
    },

    codeInputContainerError: {
      borderColor: isDark
        ? colors.dangerStrong
        : '#D94A55',
      backgroundColor: isDark
        ? colors.dangerSoft
        : '#FFF9FA',
    },

    codeInput: {
      width: '100%',
      height: '100%',
      paddingHorizontal: 0,
      color: isDark
        ? colors.text
        : '#102D52',
      fontFamily: FONT_FAMILY,
      fontSize: 26,
      fontWeight: '700',
      letterSpacing: 11,
      textAlign: 'center',
    },

    codeInputCompact: {
      fontSize: 23,
      letterSpacing: 10,
    },

    codeInputVeryCompact: {
      fontSize: 21,
      letterSpacing: 9,
    },

    supportLinkRow: {
      marginTop: 6,
      alignSelf: 'flex-start',
      // A comfortable tap target on the screen where somebody is angriest.
      paddingVertical: 6,
      paddingHorizontal: 2,
    },

    supportLinkText: {
      fontSize: 13,
      textDecorationLine: 'underline',
      color: isDark ? colors.dangerStrong : '#D5424D',
    },

    errorRow: {
      marginTop: 7,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },

    errorText: {
      flex: 1,
      color: isDark
        ? colors.dangerStrong
        : '#D5424D',
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
    },

    expiryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    },

    expiryText: {
      color: isDark
        ? colors.textMuted
        : '#8390A4',
      fontFamily: FONT_FAMILY,
      fontSize: 12.5,
      lineHeight: 18,
    },

    expiryTextVeryCompact: {
      fontSize: 11.5,
      lineHeight: 16,
    },

    /** The disk read for a recovered address is in flight; decide nothing yet. */
    recovering: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 28,
    },
    /**
     * Kept mounted but out of sight, so the code box does not lose its ref (and
     * with it the focus calls) each time the screen swaps state.
     */
    hidden: {
      display: 'none',
    },
    verifyButton: {
      height: 54,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 9,
      borderRadius: 13,
      backgroundColor: isDark
        ? colors.primary
        : '#2468D8',
      shadowColor: isDark
        ? colors.primaryStrong
        : '#205BBC',
      shadowOpacity: 0.22,
      shadowRadius: 12,
      shadowOffset: {
        width: 0,
        height: 6,
      },
      elevation: 4,
    },

    verifyButtonCompact: {
      height: 48,
      borderRadius: 12,
    },

    verifyButtonVeryCompact: {
      height: 43,
      borderRadius: 10,
    },

    verifyButtonText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      fontWeight: '700',
    },

    verifyButtonTextCompact: {
      fontSize: 15.5,
    },

    buttonPressed: {
      opacity: 0.8,
      transform: [
        {
          scale: 0.99,
        },
      ],
    },

    buttonDisabled: {
      opacity: 0.6,
    },

    resendSection: {
      alignItems: 'center',
      gap: 5,
    },

    resendSectionVeryCompact: {
      gap: 2,
    },

    resendPrompt: {
      color: isDark
        ? colors.textSecondary
        : '#7B879A',
      fontFamily: FONT_FAMILY,
      fontSize: 13,
    },

    resendPromptVeryCompact: {
      fontSize: 11.5,
    },

    resendButton: {
      minHeight: 30,
      paddingHorizontal: 9,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      borderRadius: 9,
    },

    resendButtonDisabled: {
      opacity: 0.78,
    },

    resendPressed: {
      backgroundColor: isDark
        ? colors.primarySoft
        : '#EFF5FF',
    },

    resendText: {
      color: isDark
        ? colors.primaryStrong
        : '#2463CB',
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: '700',
    },

    resentText: {
      color: isDark
        ? colors.iconMuted
        : '#8390A4',
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: '700',
    },

    backButton: {
      minHeight: 40,
      alignSelf: 'center',
      paddingHorizontal: 12,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
      borderRadius: 10,
    },

    backButtonCompact: {
      minHeight: 35,
    },

    backButtonPressed: {
      backgroundColor: isDark
        ? colors.primarySoft
        : '#EFF5FF',
    },

    backButtonText: {
      color: isDark
        ? colors.primaryStrong
        : '#2463CB',
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontWeight: '600',
    },
  });
}
